import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Heart, Shield, Users, BookOpen, Megaphone, HandHeart } from "lucide-react";
import Seo from "../lib/seo";
import { PETITION_URL, STATS } from "../lib/constants";
import AnimatedCounter from "../components/AnimatedCounter";
import Reveal from "../components/Reveal";
import PetitionCTA from "../components/PetitionCTA";
import SocialShare from "../components/SocialShare";
import ShareStorySection from "../components/ShareStorySection";
import ActionBar from "../components/ActionBar";
import MissionStatement from "../components/MissionStatement";
import PetitionProgress from "../components/PetitionProgress";
import SponsorEvent from "../components/SponsorEvent";

const HERO_IMG = "/images/hero-jclaw.png";
const HEARTBEAT_BG = "https://static.prod-images.emergentagent.com/jobs/b33e0569-2265-436b-8247-a65606b6cf84/images/ea3fd5020f81acdc910a83a801f873ec0aee9d0abeecd16129ca2e00855b0abe.png";

// Animated heartbeat ECG SVG
const HeartbeatECG = ({ className = "" }) => (
    <svg viewBox="0 0 400 30" className={`w-full h-6 ${className}`} fill="none" aria-hidden>
        <motion.path
            d="M0 15 L80 15 L100 15 L110 5 L120 25 L130 8 L140 22 L150 15 L240 15 L260 15 L270 5 L280 25 L290 8 L300 22 L310 15 L400 15"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2.4, ease: "easeInOut" }}
        />
    </svg>
);

export default function HomePage() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const heroParallaxY = useTransform(scrollYProgress, [0, 1], [0, 200]);
    const heroTextY = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    const schema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "JC Law Foundation",
        description: "Registered 501(c)(3) nonprofit advocating for AED access, CPR training, and stronger cardiac emergency preparedness in hotels through the Hotel Cardiac Emergency Preparedness Act.",
        url: "https://jclawsc.org/",
        about: {
            "@type": "NGO",
            name: "JC Law Foundation",
            nonprofitStatus: "Nonprofit501c3",
        },
    };

    return (
        <>
            <Seo
                title="Hotel Cardiac Emergency Preparedness & AED Advocacy"
                description="JC Law Foundation is a registered 501(c)(3) nonprofit advocating for AED access, CPR training, and stronger cardiac emergency preparedness in hotels through the Hotel Cardiac Emergency Preparedness Act."
                path="/"
                schema={schema}
            />

            {/* HERO — JC LAW branded graphic full-bleed */}
            <section ref={heroRef} className="relative bg-navy-900 overflow-hidden" data-testid="home-hero">
                <motion.div
                    style={{ y: heroParallaxY }}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.6, ease: [0.21, 0.47, 0.32, 0.98] }}
                    className="relative w-full"
                >
                    <img
                        src={HERO_IMG}
                        alt="JC Law — Hotel Cardiac Emergency Preparedness Act — Awareness, Preparedness, Prevention. Every Second Matters."
                        className="w-full h-auto block"
                        loading="eager"
                        fetchPriority="high"
                    />
                    {/* Soft vignette */}
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 via-transparent to-transparent pointer-events-none" />
                </motion.div>

                {/* Animated heartbeat divider at bottom of hero image */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4, duration: 1 }}
                    className="absolute bottom-3 md:bottom-6 left-0 right-0 max-w-[1400px] mx-auto px-6 lg:px-10 text-coral z-10"
                >
                    <HeartbeatECG />
                </motion.div>
            </section>

            {/* SUBHERO — Headline + CTAs + Founder promise */}
            <section className="relative bg-navy-900 text-editorial py-20 md:py-28 overflow-hidden" data-testid="home-subhero">
                <div className="absolute inset-0 opacity-10">
                    <img src={HEARTBEAT_BG} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800/70" />
                <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />

                <motion.div
                    className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-coral/10 blur-3xl pointer-events-none"
                    animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                />

                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    <div className="lg:col-span-8">
                        <Reveal>
                            <p className="overline mb-6 flex items-center gap-3" data-testid="hero-overline">
                                <span className="inline-block w-2 h-2 rounded-full bg-coral animate-heartbeat" />
                                JC Law Foundation · Every Second Matters
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl xl:text-[5rem] leading-[1.02] tracking-tighter text-balance">
                                Turning tragedy into <em className="text-gold not-italic font-serif italic">awareness,</em> advocacy, and action.
                            </h1>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <p className="mt-8 max-w-2xl text-lg md:text-xl text-editorial/80 leading-relaxed">
                                The JC Law Foundation is a registered <strong className="text-editorial">501(c)(3) nonprofit organization</strong> dedicated to improving cardiac emergency preparedness, increasing AED access, and advocating for lifesaving safety measures in hotels.
                            </p>
                        </Reveal>
                        <Reveal delay={0.4}>
                            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4" data-testid="hero-cta-row">
                                <Link
                                    to="/donate"
                                    data-testid="hero-donate-btn"
                                    className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-coral hover:bg-coral-dark text-white font-semibold transition-colors duration-300"
                                >
                                    <Heart className="w-4 h-4" />
                                    Donate Now
                                </Link>
                                <a
                                    href={PETITION_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="hero-petition-btn"
                                    className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-editorial text-navy-900 hover:bg-gold hover:text-navy-900 font-medium transition-all duration-300"
                                >
                                    Sign the Petition
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </a>
                                <Link
                                    to="/our-story"
                                    data-testid="hero-learn-btn"
                                    className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-editorial/30 text-editorial hover:border-editorial transition-all duration-300"
                                >
                                    Learn More
                                </Link>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.5} className="lg:col-span-4">
                        <div className="border-l-2 border-gold pl-6">
                            <p className="text-xs uppercase tracking-[0.22em] text-gold mb-3">A Founder's Promise</p>
                            <p className="font-serif italic text-xl leading-snug text-editorial/95">
                                "Through JC Law, I hope to turn pain into prevention by creating safer environments for travelers and families everywhere."
                            </p>
                            <p className="mt-3 text-sm text-editorial/60">— Ciera Gingery, Founder</p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* OUR MISSION STATEMENT — code-rendered */}
            <MissionStatement />

            {/* JC'S STORY snippet */}
            <section className="bg-editorial py-24 md:py-32" data-testid="home-jc-story">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden bg-navy-900/5 relative group">
                            <img src="/images/jc-real.png" alt="Joshua 'JC' — wearing his #9 jersey at sunset, a father of six" className="w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-105" loading="lazy" />
                            <div className="absolute inset-0 ring-1 ring-inset ring-gold/20 pointer-events-none" />
                            <div className="absolute bottom-4 left-4 right-4 bg-navy-900/85 backdrop-blur-sm text-editorial px-4 py-3">
                                <p className="font-serif text-lg leading-tight">Joshua "JC"</p>
                                <p className="text-[10px] uppercase tracking-[0.22em] text-gold mt-1">#9 · A father of six · Forever in our hearts</p>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-7">
                        <p className="overline mb-5 text-coral">JC's Story</p>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-navy-900 leading-[1.05] tracking-tight mb-7">
                            This is why we fight. This is why we advocate.
                        </h2>
                        <p className="text-lg md:text-xl text-navy-800/80 leading-relaxed mb-5">
                            Joshua "JC" was a father of six who had nothing but love and compassion to give. He dedicated his life to protecting others and making his community a safer place for everyone.
                        </p>
                        <p className="text-base md:text-lg text-navy-800/70 leading-relaxed mb-8">
                            In the middle of unimaginable loss, his partner Ciera was faced with the reality of how critical emergency preparedness truly is. No family should ever have to experience the feeling of helplessness during a cardiac emergency without access to trained staff or life-saving equipment.
                        </p>
                        <Link to="/our-story" data-testid="jc-story-link" className="group inline-flex items-center gap-2 text-navy-900 font-medium border-b border-navy-900 hover:border-coral hover:text-coral transition-all duration-300 pb-1">
                            Read JC's full story
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* PETITION PROGRESS — placed directly after JC's Story so visitors
                can immediately take action after connecting with his story. */}
            <PetitionProgress />

            {/* SPONSOR EVENT — 6th Annual National CPR & AED Awareness Rally & March */}
            <SponsorEvent />

            {/* Cinematic tribute section — JC's photo full-bleed parallax */}
            <section className="relative h-[70vh] md:h-[80vh] overflow-hidden bg-navy-900" data-testid="home-tribute">
                <motion.div
                    initial={{ scale: 1.15 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 2.5, ease: "easeOut" }}
                    className="absolute inset-0"
                >
                    <img src="/images/jc-real.png" alt="Joshua 'JC' standing at sunset, the inspiration behind JC Law" className="w-full h-full object-cover object-center opacity-70" loading="lazy" />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/60 to-navy-900/10" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-transparent to-navy-900/30" />
                <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />

                <div className="relative h-full flex items-center max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="max-w-2xl text-editorial">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="overline mb-6 text-gold"
                        >
                            In Loving Memory
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: 0.6, ease: [0.21, 0.47, 0.32, 0.98] }}
                            className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[1] tracking-tight"
                        >
                            For JC.<br />
                            <span className="text-gold italic">#9 — Forever.</span>
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: 1 }}
                            className="mt-8 text-lg md:text-xl text-editorial/85 max-w-xl leading-relaxed"
                        >
                            A father of six. A protector. A friend who showed up for everyone. His name lives on in every life this mission saves.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, delay: 1.3 }}
                            className="mt-10 text-coral"
                        >
                            <HeartbeatECG />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* WHY THIS MISSION MATTERS — Stats section */}
            <section className="relative bg-navy-900 text-editorial overflow-hidden" data-testid="home-stats">
                <div className="absolute inset-0">
                    <img src={HEARTBEAT_BG} alt="" className="w-full h-full object-cover opacity-15" loading="lazy" />
                </div>
                <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32">
                    <Reveal className="max-w-3xl mb-14">
                        <p className="overline mb-5">Why This Mission Matters</p>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            In a cardiac emergency, minutes mean the difference between life and loss.
                        </h2>
                    </Reveal>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                        {STATS.map((s, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.8, delay: idx * 0.12, ease: [0.21, 0.47, 0.32, 0.98] }}
                                whileHover={{ y: -4, transition: { duration: 0.3 } }}
                                className="bg-navy-900 p-8 md:p-10 group cursor-default"
                            >
                                <div className="font-serif text-5xl md:text-6xl text-gold tracking-tight group-hover:scale-105 transition-transform duration-500 origin-left">
                                    <AnimatedCounter value={s.value} prefix={s.prefix || ""} suffix={s.suffix} />
                                </div>
                                <div className="mt-4 text-sm md:text-base text-editorial/80 leading-snug">
                                    {s.label}
                                </div>
                                <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-editorial/40">
                                    {s.source}
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <Reveal delay={0.4} className="mt-14">
                        <Link to="/legislation" data-testid="stats-legislation-link" className="group inline-flex items-center gap-3 text-gold hover:text-editorial transition-colors text-lg">
                            Read the legislation behind JC Law
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* PETITION CTA */}
            <PetitionCTA variant="light" />

            {/* How You Can Help — Bento */}
            <section className="bg-editorial py-24 md:py-32" data-testid="home-help">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <Reveal className="max-w-3xl mb-14">
                        <p className="overline mb-5 text-coral">You Can Help Save Lives</p>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-navy-900 leading-[1.05] tracking-tight">
                            Every action creates impact.
                        </h2>
                        <p className="mt-6 text-lg text-navy-800/75 max-w-2xl">
                            Whether you give, volunteer, share, or simply learn — you carry forward JC's legacy of service and compassion.
                        </p>
                    </Reveal>

                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                        {/* Donate — tall */}
                        <Link to="/donate" data-testid="help-card-donate" className="group md:col-span-3 md:row-span-2 bg-navy-900 text-editorial p-10 lg:p-12 flex flex-col justify-between min-h-[400px] relative overflow-hidden hover:bg-navy-800 hover:-translate-y-1 transition-all duration-500">
                            <div>
                                <Heart className="w-8 h-8 text-coral mb-6" />
                                <h3 className="font-serif text-3xl lg:text-4xl leading-tight mb-4">Donate</h3>
                                <p className="text-editorial/75 max-w-md">Fund life-saving equipment, training programs, and community resources that carry JC's mission forward.</p>
                            </div>
                            <span className="flex items-center gap-2 text-gold group-hover:gap-3 transition-all">Give today <ArrowRight className="w-4 h-4" /></span>
                            <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-coral/10 group-hover:bg-coral/20 transition-colors duration-700" />
                        </Link>

                        <a href={PETITION_URL} target="_blank" rel="noopener noreferrer" data-testid="help-card-petition" className="group md:col-span-3 bg-editorial-muted hover:bg-gold/20 hover:-translate-y-1 p-8 lg:p-10 transition-all duration-300">
                            <Megaphone className="w-7 h-7 text-coral mb-5" />
                            <h3 className="font-serif text-2xl lg:text-3xl text-navy-900 mb-2">Sign the Petition</h3>
                            <p className="text-navy-800/70">Add your voice to require AEDs and CPR training in hotels.</p>
                        </a>

                        <Link to="/get-involved" data-testid="help-card-volunteer" className="group md:col-span-2 bg-white border border-editorial-border hover:border-navy-900 hover:-translate-y-1 p-8 transition-all duration-300">
                            <Users className="w-7 h-7 text-navy-900 mb-5" />
                            <h3 className="font-serif text-2xl text-navy-900 mb-2">Volunteer</h3>
                            <p className="text-navy-800/70 text-sm">Give your time and talent.</p>
                        </Link>

                        <Link to="/resources" data-testid="help-card-learn" className="group md:col-span-2 bg-white border border-editorial-border hover:border-navy-900 hover:-translate-y-1 p-8 transition-all duration-300">
                            <BookOpen className="w-7 h-7 text-navy-900 mb-5" />
                            <h3 className="font-serif text-2xl text-navy-900 mb-2">Learn CPR</h3>
                            <p className="text-navy-800/70 text-sm">Be the reason for a second chance.</p>
                        </Link>

                        <Link to="/contact" data-testid="help-card-partner" className="group md:col-span-2 bg-white border border-editorial-border hover:border-navy-900 hover:-translate-y-1 p-8 transition-all duration-300">
                            <HandHeart className="w-7 h-7 text-navy-900 mb-5" />
                            <h3 className="font-serif text-2xl text-navy-900 mb-2">Partner</h3>
                            <p className="text-navy-800/70 text-sm">For businesses & community leaders.</p>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Testimonial / Quote */}
            <section className="bg-editorial-muted py-24 md:py-32" data-testid="home-quote">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
                    <Reveal className="lg:col-span-7">
                        <p className="overline mb-5 text-coral">A Founder's Words</p>
                        <blockquote className="font-serif font-light text-3xl md:text-4xl lg:text-5xl text-navy-900 leading-[1.15] italic">
                            "The longest eight minutes of my life were spent performing CPR on someone I loved. No one should ever have to face a cardiac emergency alone."
                        </blockquote>
                        <div className="mt-8 flex items-center gap-5">
                            <div className="w-12 h-px bg-gold" />
                            <div>
                                <p className="font-medium text-navy-900">Ciera Gingery</p>
                                <p className="text-sm text-navy-800/60">Founder, JC Law Foundation</p>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-5">
                        <div className="bg-white p-8 border border-editorial-border">
                            <p className="overline mb-4 text-coral">Share JC's Story</p>
                            <p className="text-navy-800/80 mb-5 leading-relaxed">
                                Every share helps raise awareness. Help spread the mission and turn JC's story into a movement for safer communities.
                            </p>
                            <SocialShare testidPrefix="quote-share" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Share Your Story */}
            <ShareStorySection />

            {/* Action Bar — Quick action cells */}
            <ActionBar />

            {/* Latest Updates — coming soon */}
            <section className="bg-editorial py-24 md:py-28" data-testid="home-updates">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8 mb-12">
                    <div className="max-w-2xl">
                        <p className="overline mb-5 text-coral">Latest Awareness Updates</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-navy-900 leading-tight">From the foundation.</h2>
                    </div>
                    <Link to="/blog" className="text-sm font-medium text-navy-900 hover:text-coral border-b border-navy-900 hover:border-coral transition-colors pb-1">View all updates</Link>
                </div>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <Reveal key={i} delay={i * 0.08} className="group bg-white border border-editorial-border p-8 hover:border-navy-900 transition-colors">
                            <p className="overline mb-4 text-navy-700/60">Coming Soon</p>
                            <h3 className="font-serif text-2xl text-navy-900 mb-3 leading-snug">Awareness updates are on the way.</h3>
                            <p className="text-navy-800/70 text-sm leading-relaxed">We're preparing stories, resources, and updates from the front lines of CPR and AED advocacy. Check back soon.</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Ready to make a difference */}
            <section className="bg-navy-900 text-editorial py-24 md:py-32" data-testid="home-final-cta">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
                    <Reveal>
                        <Shield className="w-10 h-10 text-gold mx-auto mb-6" />
                        <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl mx-auto">
                            Ready to make a difference?
                        </h2>
                        <p className="mt-6 text-lg text-editorial/75 max-w-xl mx-auto">
                            Stand with us. Every name, every dollar, every share carries a life-saving mission forward.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/donate" className="px-8 py-4 bg-coral hover:bg-coral-dark text-white font-semibold transition-colors">Donate Now</Link>
                            <Link to="/get-involved" className="px-8 py-4 border border-editorial/40 hover:border-editorial transition-colors">Get Involved</Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
